import React, { useEffect, useState } from "react";
import { Alert, Card } from "@themesberg/react-bootstrap";
import Skeleton from "react-loading-skeleton";
import styles from "./index.module.css";
import TooltipWrapper from "../../helper/Tooltip";
import { FaHistory } from "react-icons/fa";
import { showNotification } from "../../service/showNotification";
import {
  getAllTrancheDocument,
  getDownloadAssetFileDetails,
  getIssueAssetFileDetails,
  getTrancheDocument,
  uploadTrancheDocument,
  updateAssetFileDetails,
} from "../../service/api";
import { IoIosEye } from "react-icons/io";
import { GlobalModal } from "../../helper/GlobalModal";
import { Button } from "react-bootstrap";
import { MdContentCopy, MdCheck } from "react-icons/md";
import { downloadFile } from "../../utils/downloadFile";
import ConfirmationModal from "../../helper/ConfirmationModal";
import { FaRegEye } from "react-icons/fa";
import { revertFileNameToLabels } from "../../utils/assetDocumentFileName";
import { RxCross2 } from "react-icons/rx";
import { CgSoftwareUpload } from "react-icons/cg";
import UploadFilePrompt from "../AssetsTableV2/UploadFilePrompt";
import TrancheDocumentLogs from "../TrancheDocumentLogs";
import { tracker } from "../../service/mixpanel";

export const previewFiles = [
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "pdf",
  "txt",
  "html",
  "mp4",
  "webm",
  "ogg",
  "mp3",
  "wav",
  // Documents
  "application/pdf",
  // Images
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/apng",
  "image/avif",
  // Audio
  "audio/mpeg", // MP3
  "audio/ogg", // Ogg Vorbis
  "audio/wav", // WAV
  "audio/webm", // WebM Audio
  "audio/aac", // AAC
  // Video
  "video/mp4", // MP4 (H.264/AAC)
  "video/webm", // WebM (VP8/VP9/Opus)
  "video/ogg", // Ogg Theora
  // Text & Code
  "text/plain",
  "text/html",
  "text/css",
  "text/javascript",
  "application/javascript",
  "application/json",
  "application/xml",
  "text/xml",
];

const AssetTableSkeleton = ({ rows = 3 }) => {
  return (
    <table className="table table-striped">
      <tbody>
        {Array.from({ length: rows }).map((_, idx) => (
          <tr key={`skeleton-${idx}`} className={styles.productionTableRow}>
            {/* REPORT NAME */}
            <td>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px",
                }}
              >
                <Skeleton
                  circle
                  width={18}
                  height={18}
                  baseColor="#e9ecef"
                  highlightColor="#f8f9fa"
                />
                <Skeleton
                  width={180}
                  height={16}
                  baseColor="#e9ecef"
                  highlightColor="#f8f9fa"
                />
              </div>
            </td>

            {/* FILE NAME */}
            <td>
              <Skeleton
                width={100}
                height={16}
                baseColor="#e9ecef"
                highlightColor="#f8f9fa"
              />
            </td>

            {/* VIEW FILE */}
            <td style={{ textAlign: "center" }}>
              <Skeleton
                circle
                width={20}
                height={20}
                baseColor="#e9ecef"
                highlightColor="#f8f9fa"
              />
            </td>

            {/* AI VALIDATED */}
            <td style={{ textAlign: "center" }}>
              <Skeleton
                circle
                width={20}
                height={20}
                baseColor="#e9ecef"
                highlightColor="#f8f9fa"
              />
            </td>

            {/* MANUAL VALIDATED */}
            <td style={{ textAlign: "center" }}>
              <Skeleton
                circle
                width={20}
                height={20}
                baseColor="#e9ecef"
                highlightColor="#f8f9fa"
              />
            </td>

            {/* ACTION */}
            <td style={{ textAlign: "center", width: "80px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "12px",
                }}
              >
                <Skeleton
                  circle
                  width={22}
                  height={22}
                  baseColor="#e9ecef"
                  highlightColor="#f8f9fa"
                />
                <Skeleton
                  circle
                  width={22}
                  height={22}
                  baseColor="#e9ecef"
                  highlightColor="#f8f9fa"
                />
                <Skeleton
                  circle
                  width={22}
                  height={22}
                  baseColor="#e9ecef"
                  highlightColor="#f8f9fa"
                />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const IssueFilePopup = ({ assetId, colId }) => {
  const [fileLink, setFileLink] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const getDetailsOfIssueFile = async (assetId, colId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getIssueAssetFileDetails(assetId, colId);

      setFileLink(data?.data?.data?.fileUrl || "");
    } catch (err) {
      console.error("Error fetching asset documents:", err);
      setError("Failed to get the details. Please try later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (fileLink) {
      tracker.tranches.trancheDocuments.copyIssueFileLinkClick(assetId, colId);
      navigator.clipboard
        .writeText(fileLink)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          showNotification("error", "Failed to copy URL.");
        });
    }
  };

  useEffect(() => {
    getDetailsOfIssueFile(assetId, colId);
  }, [assetId, colId]);

  if (loading) {
    return <div>Loading issue details...</div>;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!fileLink && !loading) {
    return <div>No issue URL found.</div>;
  }

  const shortenUrl = (url) => {
    try {
      const u = new URL(url);
      return `${u.hostname}/...${u.pathname.slice(-25)}`;
    } catch {
      return url.length > 50 ? url.slice(0, 50) + "..." : url;
    }
  };

  return (
    <Card style={{ padding: "16px" }}>
      <p style={{ marginBottom: "16px" }}>
        Issue found in the following file link. Please review:
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#f8f9fa",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "8px 12px",
        }}
      >
        <a
          href={fileLink}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "#007bff",
            flex: 1,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
          title={fileLink}
        >
          {shortenUrl(fileLink)}
        </a>

        <Button
          variant="light"
          size="sm"
          onClick={handleCopy}
          title={copied ? "Copied!" : "Copy URL"}
          disabled={copied}
          style={{ marginLeft: "8px" }}
        >
          {copied ? (
            <MdCheck size={18} style={{ color: "#0BDA51" }} />
          ) : (
            <MdContentCopy size={18} />
          )}
        </Button>
      </div>
    </Card>
  );
};

const TrancheDocumentModal = ({ trancheId, isAmpVantage }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [colId, setColId] = useState("");
  const [columns, setColumns] = useState([]);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [uploadModal, setUploadModal] = useState({
    show: false,
    doc: null,
    colId: null,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [isConfirmationModal, setIsConfirmationModal] = useState(false);
  const [modalName, setModalName] = useState("");
  const [columnOptions, setColumnOptions] = useState([]);
  const [forceUpload, setForceUpload] = useState(false);
  const [forceUploadModal, setForceUploadModal] = useState({
    show: false,
    doc: null,
    colId: null,
  });

  // 👈 ADDED VALIDATION STATES
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  const toggleModal = (name = "", assetId = "", colId) => {
    if (name === "asset_history") {
      tracker.tranches.trancheDocuments.viewTrancheHistoryClick(
        trancheId,
        colId
      );
    }
    setIsModal((prev) => !prev);
    setModalName(name);
    setColId(colId);
  };

  const toggleConfirmationModal = (name = "", id = "") => {
    setIsConfirmationModal((prev) => !prev);
    setModalName(name);
    setColId(id);
  };

  const toggleSectionCollapse = (sectionId) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const modalMap = {
    asset_history: {
      component: <TrancheDocumentLogs trancheId={trancheId} colId={colId} />,
      title: "Tranche Document History",
    },
  };

  const downloadDocument = () => {
    const url = colId;
    toggleConfirmationModal();
    downloadFile(url);
    tracker.tranches.trancheDocuments.confirmDownloadFileClick(
      trancheId,
      colId
    );
  };

  const confirmationModalMap = {
    download_file: {
      title: "Download File",
      message:
        "This file type cannot be viewed in a browser. Would you like to download this file?",
      confirmText: "Yes",
      cancelText: "No",
      onConfirm: downloadDocument,
    },
  };

  const fetchDocument = async () => {
    try {
      const res = await getAllTrancheDocument(trancheId);
      const sectionData = res?.data || [];
      setColumns(sectionData);
    } catch (err) {
      showNotification("error", "Failed to fetch sections.");
    } finally {
      setLoading(false);
    }
  };

  const getAllDocuments = async () => {
    try {
      const data = await getTrancheDocument(trancheId);
      // The API returns { data: [...], message: "Success", success: true }
      setDocuments(data?.data?.data || data?.data || []);
    } catch (err) {
      showNotification("error", "Failed to load asset documents.");
    }
  };

  // 👈 ADDED VALIDATION FUNCTION
  const handleCheckboxChange = async (id, body) => {
    setDocuments((prev) =>
      prev.map((d) => (d._id === id ? { ...d, ...body } : d))
    );
    try {
      const response = await updateAssetFileDetails(id, body);
      await getAllDocuments();
      showNotification("success", "Tranche checkbox updated successfully.");
    } catch (err) {
      showNotification("error", "Failed to update tranche checkbox.");
      setDocuments((prev) =>
        prev.map((d) =>
          d._id === id
            ? {
                ...d,
                ...(body.aiValidated ? { aiValidated: false } : {}),
                ...(body.manualValidated ? { manualValidated: false } : {}),
              }
            : d
        )
      );
    }
  };

  // Handle file upload - similar to AssetDocumentModal
  useEffect(() => {
    const uploadFile = async () => {
      if (!selectedFile || !uploadModal.colId) return;
      setUploading(true);
      try {
        await uploadTrancheDocument(
          trancheId,
          uploadModal.colId,
          selectedFile,
          forceUpload
        );
        showNotification("success", "File uploaded successfully.");
        await getAllDocuments();
        setUploadModal({ show: false, doc: null, colId: null });
        setSelectedFile(null);
      } catch (err) {
        const errMessage = err?.response?.data?.message || "";
        if (errMessage.includes("File too large")) {
          showNotification("error", errMessage);
        } else {
          showNotification("error", "Failed to upload file.");
        }
      } finally {
        setUploading(false);
        setForceUpload(false);
      }
    };
    uploadFile();
  }, [selectedFile, uploadModal.colId, trancheId]);

  useEffect(() => {
    const fetchData = async () => {
      await fetchDocument();
      await getAllDocuments();
    };
    fetchData();
  }, [trancheId]);

  const handleDownloadGetDownload = async (id) => {
    try {
      const response = await getDownloadAssetFileDetails(id);
      const downloadUrl = response?.data?.url;
      if (!downloadUrl) return;

      const ext = downloadUrl.split(".").pop()?.toLowerCase();

      if (
        (ext && previewFiles.includes(ext)) ||
        previewFiles.includes(response?.data?.type)
      ) {
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      } else {
        toggleConfirmationModal("download_file", downloadUrl);
      }
    } catch (err) {
      showNotification(
        "error",
        err?.response?.data?.message || "Failed to download file."
      );
    }
  };

  const getDisplayFileName = (fileName) => {
    if (!fileName) return "";
    if (!columnOptions?.length) return fileName;
    return revertFileNameToLabels(fileName, columnOptions);
  };

  const handleUploadClick = (doc, colId) => {
    if (doc && (doc.manualValidated || doc.aiValidated)) {
      setForceUpload(false);
      setForceUploadModal({ show: true, doc, colId });
      return;
    }

    tracker.tranches.trancheDocuments.uploadFileClick(trancheId, colId, !!doc);
    setUploadModal({ show: true, doc, colId });
  };

  return (
    <>
      <div className={isModal ? styles.faded : ""}>
        <div className={styles.pdfListContainer}>
          {loading ? (
            <div>
              <h5>
                <Skeleton width={100} height={20} />
              </h5>
              <AssetTableSkeleton rows={1} />
            </div>
          ) : (
            <div className={styles.tableScroll}>
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>REPORT NAME</th>
                    <th>FILE NAME</th>
                    <th style={{ textAlign: "center" }}>VIEW FILE</th>
                    <th style={{ textAlign: "center" }}>NOT REQUIRED</th>
                    <th style={{ textAlign: "center" }}>AI VALIDATED</th>
                    <th style={{ textAlign: "center" }}>MANUAL VALIDATED</th>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.map((col) => {
                    const matchedDoc = documents.find((doc) => {
                      // Handle both string comparison and object comparison
                      const docColId =
                        typeof doc.colId === "string"
                          ? doc.colId
                          : doc.colId?._id;
                      const docTrancheColId =
                        typeof doc.trancheColumnId === "string"
                          ? doc.trancheColumnId
                          : doc.trancheColumnId?._id;

                      return (
                        docColId === col._id ||
                        docTrancheColId === col._id ||
                        doc.colId === col._id ||
                        doc.colId?._id === col._id
                      );
                    });

                    const fileName = getDisplayFileName(
                      matchedDoc?.fileName || col.fileName,
                      columnOptions
                    );
                    const isRequired =
                      isAmpVantage || col.label === "Bill of Sale";

                    return (
                      <tr key={col._id} className={styles.productionTableRow}>
                        <td>
                          <p className={styles.productionReportFileStyle}>
                            {col.label}{" "}
                            {isRequired && (
                              <span className={styles.requiredStar}>*</span>
                            )}
                          </p>
                        </td>
                        <td>
                          <p className={styles.productionReportFileStyle}>
                            {fileName}
                          </p>
                        </td>

                        <td style={{ textAlign: "center" }}>
                          {matchedDoc ? (
                            matchedDoc.status === "failure" ? (
                              <IoIosEye
                                size={20}
                                onClick={() => {
                                  tracker.tranches.trancheDocuments.viewIssueFileClick(
                                    trancheId,
                                    col._id
                                  );
                                  setShowModal(true);
                                  setColId(col._id);
                                }}
                              />
                            ) : (
                              <FaRegEye
                                size={20}
                                title="Download file"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  tracker.tranches.trancheDocuments.downloadTrancheFileClick(
                                    trancheId,
                                    col._id
                                  );
                                  handleDownloadGetDownload(matchedDoc._id);
                                }}
                              />
                            )
                          ) : (
                            ""
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            className={styles.checkboxGreen}
                            checked={!isRequired}
                            disabled={isRequired}
                          />
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {matchedDoc?.aiValidatedDate ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                justifyContent: "center",
                              }}
                            >
                              {matchedDoc.aiValidated ? (
                                <MdCheck size={20} style={{ color: "green" }} />
                              ) : (
                                <RxCross2 size={20} style={{ color: "red" }} />
                              )}
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#6c757d",
                                }}
                              >
                                {new Date(
                                  matchedDoc.aiValidatedDate
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          ) : (
                            <input
                              type="checkbox"
                              disabled={!matchedDoc || matchedDoc.aiValidated}
                              checked={matchedDoc?.aiValidated || false}
                              className={styles.checkboxBlue}
                              onChange={() => {
                                tracker.tranches.trancheDocuments.validateAiClick(
                                  trancheId,
                                  col._id
                                );
                                if (!matchedDoc || matchedDoc.aiValidated)
                                  return;
                                setPendingUpdate({
                                  id: matchedDoc._id,
                                  body: { aiValidated: true },
                                });
                                setConfirmModalOpen(true);
                              }}
                            />
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {matchedDoc?.manualValidated ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                justifyContent: "center",
                              }}
                            >
                              <MdCheck size={20} style={{ color: "green" }} />

                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#6c757d",
                                }}
                              >
                                {new Date(
                                  matchedDoc.manualValidatedDate
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          ) : (
                            <input
                              type="checkbox"
                              disabled={
                                !matchedDoc || matchedDoc.manualValidated
                              }
                              checked={matchedDoc?.manualValidated || false}
                              className={styles.checkboxBlue}
                              onChange={() => {
                                tracker.tranches.trancheDocuments.validateManualClick(
                                  trancheId,
                                  col._id
                                );
                                if (!matchedDoc || matchedDoc.manualValidated)
                                  return;
                                setPendingUpdate({
                                  id: matchedDoc._id,
                                  body: { manualValidated: true },
                                });
                                setConfirmModalOpen(true);
                              }}
                            />
                          )}
                        </td>
                        <td style={{ textAlign: "center", width: "120px" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              gap: "12px",
                            }}
                          >
                            <TooltipWrapper
                              tooltip={
                                matchedDoc
                                  ? "Replace document"
                                  : "Upload document"
                              }
                              children={
                                <CgSoftwareUpload
                                  size={22}
                                  style={{ cursor: "pointer" }}
                                  title="Upload or replace document"
                                  onClick={() => {
                                    handleUploadClick(matchedDoc, col._id);
                                  }}
                                />
                              }
                            />
                            <TooltipWrapper
                              tooltip={"View history"}
                              children={
                                <FaHistory
                                  onClick={() => {
                                    setColId(col._id);
                                    toggleModal(
                                      "asset_history",
                                      trancheId,
                                      col._id
                                    );
                                  }}
                                />
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 👈 ADDED VALIDATION CONFIRMATION MODAL */}
      <ConfirmationModal
        onConfirm={() => {
          if (pendingUpdate) {
            const validationType = pendingUpdate.body.aiValidated
              ? "AI"
              : "Manual";
            tracker.tranches.trancheDocuments.confirmValidationClick(
              trancheId,
              pendingUpdate.id,
              validationType
            );
            handleCheckboxChange(pendingUpdate.id, pendingUpdate.body);
          }
          setConfirmModalOpen(false);
          setPendingUpdate(null);
        }}
        onHide={() => {
          setConfirmModalOpen(false);
          setPendingUpdate(null);
        }}
        show={confirmModalOpen}
        title="Confirmation"
        message="This checkbox cannot be unchecked again."
        confirmText="Confirm"
      />

      {/* Global and Confirmation Modals */}
      <GlobalModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Issue File"
        size="lg"
      >
        <IssueFilePopup assetId={trancheId} colId={colId} />
      </GlobalModal>

      <GlobalModal
        isOpen={uploadModal.show}
        onClose={() => {
          setUploadModal({ show: false, doc: null, colId: null });
          setSelectedFile(null);
        }}
        title="Upload Document"
        size="md"
      >
        <UploadFilePrompt
          hasDoc={!!uploadModal.doc}
          uploading={uploading}
          onCancel={() => {
            setUploadModal({ show: false, doc: null, colId: null });
            setSelectedFile(null);
          }}
          onPickFile={() => {
            tracker.trancheDocuments.uploadFileSubmitClick(
              trancheId,
              uploadModal.colId
            );
            document.getElementById("fileInputRef").click();
          }}
          onFileChange={(file) => setSelectedFile(file)}
          forceUpload={forceUpload}
        />
      </GlobalModal>
      <ConfirmationModal
        show={forceUploadModal.show}
        onHide={() => {
          setForceUploadModal({ show: false, doc: null, colId: null });
          setForceUpload(false);
        }}
        onConfirm={() => {
          setUploadModal({
            show: true,
            doc: forceUploadModal.doc,
            colId: forceUploadModal.colId,
            forceUpload: true,
          });

          setForceUploadModal({ show: false, doc: null, colId: null });
        }}
        title="Replace Validated Document"
        confirmText="Continue"
        disabledConfirm={!forceUpload}
        message="Please check the box below to confirm that you want to replace the existing document."
      >
        <div className={styles.forceUploadWarningBox}>
          <p className={styles.forceUploadWarningText}>
            This document is already <strong>AI or Manually validated</strong>.
            Replacing it may invalidate previous checks.
          </p>

          <label className={styles.forceUploadCheckboxWrapper}>
            <input
              type="checkbox"
              className={styles.forceUploadCheckbox}
              checked={forceUpload}
              onChange={(e) => setForceUpload(e.target.checked)}
            />
            <span className={styles.forceUploadCheckboxLabel}>
              I understand and want to force upload the new document.
            </span>
          </label>
        </div>
      </ConfirmationModal>
      <GlobalModal
        isOpen={isModal}
        onClose={() => toggleModal()}
        children={modalMap[modalName]?.component}
        title={modalMap[modalName]?.title || "Something went wrong"}
      />

      <ConfirmationModal
        show={isConfirmationModal}
        onHide={() => toggleConfirmationModal()}
        onConfirm={confirmationModalMap[modalName]?.onConfirm}
        title={confirmationModalMap[modalName]?.title || "Something went wrong"}
        message={confirmationModalMap[modalName]?.message}
        confirmText={confirmationModalMap[modalName]?.confirmText}
        cancelText={confirmationModalMap[modalName]?.cancelText}
      />
    </>
  );
};

export default TrancheDocumentModal;
